import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getCaseStudyBySlug } from "@/lib/case-studies.functions";
import { seo } from "@/lib/seo";
import { breadcrumb, article, portableTextWordCount } from "@/lib/structured-data";
import { schemaImages, FALLBACK_SCHEMA_IMAGES } from "@/lib/sanity-image";
import { PortableText } from "@/lib/portable-text";
import { NotFoundPage } from "@/components/not-found-page";
import { SITE_URL } from "@/lib/seo-constants";
import { urlFor, type CaseStudy } from "@/lib/sanity";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: async ({ params }): Promise<CaseStudy> => {
    const post = await getCaseStudyBySlug({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData, params }) => {
    const path = `/case-studies/${params.slug}`;
    if (!loaderData) {
      return seo({
        title: "Case study not found",
        description: "The case study you're looking for doesn't exist.",
        path,
        noindex: true,
      });
    }
    const post = loaderData as CaseStudy;
    const hasBody = Array.isArray(post.body) && post.body.length > 0;
    const slug = (post.slug?.current ?? "").toString();
    const crumbs = [
      { name: "Home", url: "/" },
      { name: "Case studies", url: "/case-studies" },
      { name: post.title, url: `/case-studies/${slug}` },
    ];
    // The GROQ projection dereferences the asset (`coverImage{ asset->{ url } }`),
    // so this is already the `{ asset: { url } }` shape `schemaImages` expects.
    const images = post.coverImage
      ? schemaImages(post.coverImage as Parameters<typeof schemaImages>[0])
      : FALLBACK_SCHEMA_IMAGES;
    // Case studies are written by the company, not by a founder in a personal
    // capacity, so the byline is the Organization (`authorOrg`). `caseStudy` has
    // no editorial publish date field — `_createdAt` is the closest real signal.
    const articleLd = article({
      headline: post.title,
      description: post.excerpt,
      url: `${SITE_URL}/case-studies/${slug}`,
      image: images,
      datePublished: post._createdAt,
      dateModified: post._updatedAt,
      authorOrg: true,
      articleSection: post.category,
      wordCount: portableTextWordCount(post.body),
      keywords: post.keywords,
      type: "Article",
    });
    const augmented = {
      ...articleLd,
      ...(post.company ? { about: { "@type": "Organization", name: post.company } } : {}),
      mentions: [
        { "@type": "SoftwareApplication", "@id": `${SITE_URL}/#axy-app`, name: "Axy.digital" },
      ],
    };
    const jsonLd = [breadcrumb(crumbs), augmented];
    return seo({
      title: post.title,
      description: post.excerpt ?? "",
      path,
      type: "article",
      noindex: !hasBody,
      publishedTime: post._createdAt,
      modifiedTime: post._updatedAt,
      sanityMeta: post as Parameters<typeof seo>[0]["sanityMeta"],
      jsonLd,
    });
  },
  notFoundComponent: NotFoundPage,
  component: CaseStudyPostPage,
});

function CaseStudyPostPage() {
  const post = Route.useLoaderData() as CaseStudy;
  const hasBody = Array.isArray(post.body) && post.body.length > 0;
  const imgUrl = post.coverImage
    ? urlFor(post.coverImage as Parameters<typeof urlFor>[0])
        .width(1200)
        .height(630)
        .fit("crop")
        .auto("format")
        .url()
    : null;

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 text-white">
      <nav className="text-sm text-white/50 mb-8 flex items-center gap-2">
        <Link to="/" className="hover:text-white transition-colors">
          Home
        </Link>
        <span aria-hidden>›</span>
        <Link to="/case-studies" className="hover:text-white transition-colors">
          Case studies
        </Link>
        <span aria-hidden>›</span>
        <span className="text-white/80 truncate">{post.title}</span>
      </nav>

      {post.category && (
        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-pink-soft mb-4">
          {post.category}
        </p>
      )}

      <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6 leading-tight">
        {post.title}
      </h1>

      <div className="flex items-center gap-3 text-sm text-white/50 mb-8">
        {post.company && <span>{post.company}</span>}
        {post.company && post.metric && <span aria-hidden>·</span>}
        {post.metric && <span className="text-pink-soft font-medium">{post.metric}</span>}
      </div>

      {imgUrl && (
        <img
          src={imgUrl}
          alt={post.title}
          className="w-full rounded-lg object-cover mb-10 aspect-video"
          loading="eager"
        />
      )}

      {hasBody ? (
        <PortableText value={post.body} className="space-y-0" />
      ) : (
        <div className="space-y-6">
          {post.excerpt && <p className="text-lg text-white/75 leading-relaxed">{post.excerpt}</p>}
          {post.externalUrl && (
            <a
              href={post.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-pink-soft hover:text-white transition-colors font-medium"
            >
              Read the full case study →
            </a>
          )}
        </div>
      )}

      <div className="mt-16 pt-8 border-t border-white/10">
        <Link
          to="/case-studies"
          className="text-sm text-pink-soft hover:text-white transition-colors"
        >
          ← Back to Case studies
        </Link>
      </div>
    </article>
  );
}
